import React, { useMemo, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getFromLocalStorage, removeFromLocalStorage } from "../utils/localStorage";
import { User, LogOut, Package } from 'lucide-react';
import logo from '../assets/Images/logo.png';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Layout component only handles user authentication
  const token = getFromLocalStorage("token");
  const user = getFromLocalStorage("user");
  const isLoggedIn = !!(token && token.trim() !== "");
  const currentUser = user;

  // Derive role from local storage and membership plan (consistent with Header)
  const computeRoleFromLocalData = () => {
    try {
      const storedRole = localStorage.getItem("userRole");
      const userRaw = localStorage.getItem("user");
      let derived = null;
      if (userRaw) {
        const u = typeof userRaw === 'string' ? JSON.parse(userRaw) : userRaw;
        const userType = u?.user_type || u?.role || 'user';
        const membershipPlan = u?.membershipPlan || 'free';
        const isCreator = u?.isCreator === true || userType === 'creator';

        // CRITICAL FIX: Check for creator status more comprehensively
        if (isCreator && membershipPlan === 'pro') {
          derived = 'creator_pro';
        } else if (isCreator) {
          derived = 'creator';
        } else {
          derived = 'user';
        }
      }
      const finalRole = storedRole || derived || 'user';
      // Update localStorage if role was derived but not stored
      if (finalRole && finalRole !== storedRole) {
        localStorage.setItem('userRole', finalRole);
      }
      return finalRole;
    } catch {
      return localStorage.getItem('userRole') || 'user';
    }
  };

  const userRole = computeRoleFromLocalData();
  const isCreatorOrPro = userRole === 'creator' || userRole === 'creator_pro';

  const onAdminRoute = location.pathname.startsWith('/admin');
  const isCreator = useMemo(() => {
    if (!isLoggedIn) return false;
    if (!user) return false;
    // Check multiple fields for creator status
    return user.isCreator === true || user.user_type === 'creator' || userRole === 'creator' || userRole === 'creator_pro';
  }, [isLoggedIn, user, userRole]);

  const handleLogout = useCallback(() => {
    // Only clear user tokens from Layout component (user-side logout)
    removeFromLocalStorage("token");
    removeFromLocalStorage("user");
    removeFromLocalStorage("userRole");
    navigate("/");
    window.location.reload();
  }, [navigate]);

  const shouldHideHeader = (path) => {
    const hiddenPaths = ['/overlay', '/admin/overlay', '/login', '/signup', '/privacy', '/tnc'];
    // Check for exact match or match with trailing slash
    return hiddenPaths.some(p => path === p || path === `${p}/`) || onAdminRoute;
  };

  const hideHeader = shouldHideHeader(location.pathname);

  return (
    <div className="min-h-screen bg-black text-white">
      {!hideHeader && (
        <header className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-xl border-b border-yellow-500/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              {/* Logo */}
              <Link to="/" className="flex items-center">
                <img
                  src={logo}
                  alt="Crezar Logo"
                  className="w-12 h-12 md:w-16 md:h-16 object-contain rounded-xl"
                />
                <span className="text-2xl font-bold bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent">
                  CREZAR
                </span>
              </Link>

              {/* Main Navigation */}
              <nav className="hidden md:flex items-center space-x-8">
                <Link to="/" className="text-white hover:text-yellow-400 transition-all duration-300 relative group">
                  Home
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
                </Link>
                <Link to="/dashboard" className={`transition-all duration-300 relative group ${location.pathname.startsWith('/dashboard') ? 'text-yellow-400' : 'text-white hover:text-yellow-400'}`}>
                  Dashboard
                  <span className={`absolute -bottom-1 left-0 ${location.pathname.startsWith('/dashboard') ? 'w-full' : 'w-0 group-hover:w-full'} h-0.5 bg-yellow-400 transition-all duration-300`}></span>
                </Link>
                <Link to="/streamers" className="text-white hover:text-yellow-400 transition-all duration-300 relative group">
                  Streamers
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
                </Link>
                <Link to="/my-cards" className="text-white hover:text-yellow-400 transition-all duration-300 relative group">
                  My Cards
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
                </Link>
                {(!onAdminRoute && isCreator) && (
                  <Link to="/challenges" className="text-white hover:text-yellow-400 transition-all duration-300 relative group">
                    Challenges
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                )}
                {onAdminRoute ? (
                  <>
                    <Link to="/admin/broadcast-card-management" className="text-white hover:text-yellow-400 transition-all duration-300 relative group">
                      Broadcast
                      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
                    </Link>
                    <Link to="/admin/overlay" className="text-white hover:text-yellow-400 transition-all duration-300 relative group">
                      Overlay
                      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
                    </Link>
                  </>
                ) : (!onAdminRoute && isCreatorOrPro && (
                  <Link to="/overlay" className="text-white hover:text-yellow-400 transition-all duration-300 relative group">
                    Overlay
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                ))}
                <Link to="/contactus" className="text-white hover:text-yellow-400 transition-all duration-300 relative group">
                  Contact
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </nav>

              {/* User Actions - Only show on non-admin routes */}
              {!onAdminRoute && (
                <div className="hidden md:flex items-center space-x-4">
                  {isLoggedIn ? (
                    <>
                      <Link to="/profile" className="text-white hover:text-yellow-400 transition-colors flex items-center space-x-1">
                        <User className="w-4 h-4" />
                        <span>Profile</span>
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="text-white hover:text-yellow-400 transition-colors flex items-center space-x-1"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" className="text-white hover:text-yellow-400 transition-colors">
                        Sign In
                      </Link>
                      <Link
                        to="/signup"
                        className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-6 py-2 rounded-lg font-semibold hover:from-yellow-500 hover:to-yellow-700 transition-all transform hover:scale-105 shadow-lg shadow-yellow-500/25"
                      >
                        Get Started
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>
      )}

      <main className={`${!hideHeader ? 'pt-16' : ''}`}>
        {children}
      </main>
    </div>
  );
};

export default Layout;
